import React, { useEffect, useMemo, useState } from 'react';
import { Check } from '@untitled-ui/icons-react';
import classNames from 'classnames';
import moment, { Moment } from 'moment';
import { useAsyncData } from '@myhealth-raccoon/services';
import { Button, Spinner, Tooltip } from '@myhealth-raccoon/components';

import {
    AppointmentType,
    AutomedClinicAvailabilityTimeModel,
    availabilityService,
    SiteBookingAvailabilityModel,
    UserBookingDetails
} from 'src/services/availability';
import { money, subtractSurchargeFromFee } from 'src/utils/renderMoney';

type Props = {
    appointmentTypes: AppointmentType[];
    preselectedAppointmentType?: AppointmentType,
    siteId: string | number;
    className?: string;
    onSelectTimeSlot(appointment: { date: Date, doctorId: number[], clinicId: string | number, appointmentTypeId: string, timeSlot: AutomedClinicAvailabilityTimeModel }): void;
    selected?: Moment;
}

type AppointmentTypeTimesProps = {
    appointmentType: AppointmentType,
    siteId: string | number,
    onSelectTimeSlot(appointment: { date: Date, doctorId: number[], clinicId: string | number, appointmentTypeId: string, timeSlot: AutomedClinicAvailabilityTimeModel }): void;
    selected?: Moment;
}

function AppointmentTypeTimes({ appointmentType, siteId, onSelectTimeSlot, selected }: AppointmentTypeTimesProps) {

    const dates = useMemo(() => ({
        startDate: moment().toDate(), endDate: moment().add(12, "hours").toDate()
    }), [])

    const { data, ...request } = useAsyncData({
        method: availabilityService.GetBookingTimeslot,
        service: availabilityService,
        selector: (data: SiteBookingAvailabilityModel[]) => {
            return data.reduce((curr, doc) => {
                for (const [date, timeSlots] of Object.entries(doc.timeSlots)) {
                    for (const ts of timeSlots) {
                        const dateTime = moment(`${date} ${ts.t}`);
                        const existingTimeslot = curr.find(x => x.dateTime.isSame(dateTime, "minute"))

                        if (existingTimeslot) {
                            existingTimeslot.users.push(doc.user);
                        } else {
                            curr.push({
                                ts,
                                dateTime,
                                duration: ts.d,
                                users: [doc.user]
                            });
                        }
                    }
                }
                return curr;
            }, [] as {
                dateTime: Moment,
                duration: string,
                users: UserBookingDetails[],
                ts: AutomedClinicAvailabilityTimeModel,
            }[])
                .sort((a, b) => a.dateTime.valueOf() - b.dateTime.valueOf())
                .filter(({ dateTime }) => dateTime.diff(undefined, "hours") < 12)
        }
    }, siteId, appointmentType.appointmentTypeId, null, dates);

    return request.isLoading
        ? <div className='h-44 flex items-center justify-center'><Spinner size='md' /></div>
        : data?.length ? <div className='grid sm:grid-cols-4 grid-cols-3 gap-2'>
            {data.map(({ dateTime, duration, users, ts }) => <Tooltip
                key={dateTime.valueOf()}
                attachmentPoint="right"
                tooltipContent={<>{dateTime.format("ddd, D MMM")}<br />{dateTime.format("LT")} ({duration} mins)</>}
            >
                <Button
                    color={selected?.isSame(dateTime, 'minute') ? "primary" : "gray"}
                    className={classNames('w-full relative !py-2 !px-0.5', {
                        'hover:!text-primary hover:!border-primary hover:!bg-white': !selected?.isSame(dateTime, 'minute')
                    })}
                    onClick={() => onSelectTimeSlot({
                        date: dateTime.toDate(),
                        doctorId: users.map(d => d.userId),
                        appointmentTypeId: appointmentType.appointmentTypeId,
                        clinicId: siteId,
                        timeSlot: ts
                    })}
                >
                    {dateTime.format("LT")}
                </Button>
            </Tooltip>)}
        </div>
            : <div className='h-44 flex items-center justify-center font-medium text-error text-center'>
                {request.error
                    ? request.error.message
                    : "There are no times available for this appointment type."}
            </div>;
}

export function AfterHoursAvailability({ appointmentTypes, preselectedAppointmentType, siteId, className, onSelectTimeSlot, selected: selectedTime }: Props) {

    const [selected, setSelected] = useState(preselectedAppointmentType || appointmentTypes[0]);
    // when the prop changes, change the selected
    useEffect(() => {
        if (preselectedAppointmentType) {
            setSelected(preselectedAppointmentType);
        }
    }, [preselectedAppointmentType])

    const { max, min } = useMemo(() => appointmentTypes.reduce(({ min, max }, at) => (
        {
            min: Math.min(min, subtractSurchargeFromFee(Number(at.prePaymentFees) + Number(at.postBillingFees))),
            max: Math.max(max, subtractSurchargeFromFee(Number(at.prePaymentFees) + Number(at.postBillingFees))),
        }), { max: 0, min: Infinity }), [appointmentTypes]);

    return <>
        <div className={classNames(className, 'space-y-6')}>
            {appointmentTypes.map((at) => {
                const fee = subtractSurchargeFromFee(Number(at.prePaymentFees) + Number(at.postBillingFees));
                const scaledFee = (fee - min) / (max - min);
                return <div key={at.appointmentTypeId}
                    className={classNames('rounded-xl bg-white', {
                        'border border-gray-200': at !== selected,
                        'border border-primary-600 ring-1 ring-primary-600': at === selected,
                    })}
                >
                    <button className={classNames('flex flex-row p-4 gap-2.5 items-center w-full text-left', {

                    })}
                        onClick={at === selected ? undefined : () => setSelected(at)}
                        disabled={at === selected}
                    >
                        <h3 className='flex-1 font-bold text-base text-gray-700'>{at.appointmentName}</h3>
                        <p className={classNames(' py-1 px-2.5  font-medium rounded-lg', {
                            'bg-success-50 text-success-700': scaledFee < 0.33,
                            'bg-warning-50 text-warning-700': scaledFee >= 0.33 && scaledFee < 0.66,
                            'bg-error-50 text-error-700': scaledFee >= 0.66,
                        })}>${money(fee)}</p>
                        {at === selected
                            ? <i className='flex w-4 h-4 items-center justify-center rounded-full bg-primary relative' >
                                <Check className='block h-3 w-3 text-white absolute intset-0 m-auto' />
                            </i>
                            : <i className='block w-4 h-4 rounded-full border-gray-300 border'></i>
                        }
                    </button>
                    {at === selected && <>
                        <hr className='border-t-2 border-primary-600' />
                        <div className='p-4'>
                            <AppointmentTypeTimes appointmentType={at} siteId={siteId} onSelectTimeSlot={onSelectTimeSlot} selected={selectedTime} />
                        </div>
                    </>}
                </div>
            })}
        </div>
    </>
}